/**
 * Weapon ribbon trails (pooled, shared by every hero). Each trail keeps a short history of
 * (blade tip, inner edge) pairs sampled from the real weapon each frame. It draws them as a
 * two-rail ribbon, smoothed with Catmull-Rom subdivision. Points fade by age: alpha falls off, the
 * colour moves from the head to the tail colour, and the band narrows toward the tip.
 *
 * Two draw calls at most (additive and alpha-blended batches). All buffers are allocated once.
 * `push` and `update` don't allocate. Brightness is moderate on purpose: the shape and motion carry
 * the impact, and the effects-intensity setting scales the opacity.
 */
import * as THREE from 'three';
import type { RGB } from '../anim/clips';

const VERT = `
attribute vec2 aUv; attribute vec4 aColor; attribute float aStyle;
varying vec2 vUv; varying vec4 vCol; varying float vStyle;
void main(){ vUv = aUv; vCol = aColor; vStyle = aStyle; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

// vUv.x: 0 at the blade (newest) .. 1 at the tail (oldest); vUv.y: 0 at the tip edge .. 1 inner edge.
const FRAG = `
uniform float uFx; uniform float uTime;
varying vec2 vUv; varying vec4 vCol; varying float vStyle;
void main(){
  float u = vUv.x;
  float v = vUv.y;
  float shape;
  float core;
  if (vStyle < 0.5) {
    // crescent: solid band, bright leading edge at the tip, soft toward the hilt
    float edge = smoothstep(0.24, 0.0, v);
    shape = smoothstep(1.0, 0.25, v) * 0.62 + edge * 0.45;
    core = edge * (1.0 - u);
  } else if (vStyle < 1.5) {
    // streak: thin parallel speed lines
    float lines = pow(0.5 + 0.5 * sin(v * 31.4 + 1.3), 6.0);
    shape = lines * 0.85 + smoothstep(0.25, 0.0, v) * 0.55 + 0.2 * (1.0 - v);
    core = smoothstep(0.14, 0.0, v) * (1.0 - u);
  } else if (vStyle < 2.5) {
    // electric: a jagged bolt running through the band inside a soft halo, flickering. The bolt
    // is thick enough (and the halo wide enough) to still read at game-camera distance.
    float n = sin(u * 47.0 - uTime * 37.0) * 0.6 + sin(u * 113.0 + uTime * 23.0) * 0.4;
    float d = abs(v - (0.42 + 0.3 * n));
    float bolt = smoothstep(0.17, 0.02, d);
    float halo = smoothstep(0.45, 0.0, d);
    float flick = step(0.22, fract(sin(floor(uTime * 30.0) * 12.9898) * 43758.5453));
    shape = bolt * (0.75 + 0.35 * flick) + halo * 0.32 + smoothstep(0.22, 0.0, v) * 0.55 + 0.22 * (1.0 - v);
    core = bolt * 0.9;
  } else {
    // holy: soft wide glow with drifting sparkle bands
    float glow = smoothstep(1.0, 0.0, v);
    float bands = smoothstep(0.85, 1.0, sin(u * 26.0 - uTime * 5.0 + v * 4.0));
    shape = glow * 0.75 + bands * 0.4 * glow + smoothstep(0.2, 0.0, v) * 0.4;
    core = smoothstep(0.2, 0.0, v) * 0.6;
  }
  shape *= smoothstep(0.0, 0.035, v);
  vec3 col = mix(vCol.rgb, vec3(1.0, 0.96, 0.9), clamp(core, 0.0, 1.0) * 0.4);
  float a = vCol.a * shape * uFx;
  if (a < 0.003) discard;
  gl_FragColor = vec4(col, min(a, 1.0));
}`;

/**
 * One update range per attribute, reused every frame: three.js clears `updateRanges` after each
 * upload, and `addUpdateRange` allocates a new range object. Here the clear is a no-op and the
 * single range is edited in place, so uploads make no garbage.
 */
function persistentRange(attr: THREE.BufferAttribute): void {
  attr.addUpdateRange(0, 0);
  attr.clearUpdateRanges = function (): void {};
}

function upload(attr: THREE.BufferAttribute, n: number): void {
  const r = attr.updateRanges[0]!;
  r.start = 0;
  r.count = n;
  attr.needsUpdate = true;
}

/** Newest sample is overwritten (not pushed) when the last one is younger than this. */
const MIN_SAMPLE_DT = 1 / 90;

class TrailBatch {
  readonly mesh: THREE.Mesh;
  /** Pairs (tip + inner vertex) per trail. */
  private readonly MAXP: number;
  private readonly sTip: Float32Array;
  private readonly sIn: Float32Array;
  private readonly sAge: Float32Array;
  private readonly count: Int32Array;
  private readonly sampling: Uint8Array;
  private readonly used: Uint8Array;
  /** Pairs written last frame (cleared when the trail shrinks). */
  private readonly lastPairs: Int32Array;
  /** Per trail: head rgb, tail rgb, styleId, fade, alpha. */
  private readonly par: Float32Array;
  private readonly freeList: Int32Array;
  private freeTop: number;
  private readonly geo = new THREE.BufferGeometry();
  private readonly aPos: THREE.BufferAttribute;
  private readonly aUv: THREE.BufferAttribute;
  private readonly aCol: THREE.BufferAttribute;
  private readonly aStyle: THREE.BufferAttribute;
  private readonly mat: THREE.ShaderMaterial;
  live = 0;

  constructor(readonly maxTrails: number, readonly maxSamples: number, readonly sub: number, uniforms: { uFx: { value: number }; uTime: { value: number } }, additive: boolean) {
    this.MAXP = (maxSamples - 1) * sub + 1;
    const nS = maxTrails * maxSamples;
    this.sTip = new Float32Array(nS * 3);
    this.sIn = new Float32Array(nS * 3);
    this.sAge = new Float32Array(nS);
    this.count = new Int32Array(maxTrails);
    this.sampling = new Uint8Array(maxTrails);
    this.used = new Uint8Array(maxTrails);
    this.lastPairs = new Int32Array(maxTrails);
    this.par = new Float32Array(maxTrails * 9);
    this.freeList = new Int32Array(maxTrails);
    for (let i = 0; i < maxTrails; i++) this.freeList[i] = maxTrails - 1 - i;
    this.freeTop = maxTrails;
    const nV = maxTrails * this.MAXP * 2;
    this.aPos = new THREE.BufferAttribute(new Float32Array(nV * 3), 3).setUsage(THREE.DynamicDrawUsage);
    this.aUv = new THREE.BufferAttribute(new Float32Array(nV * 2), 2).setUsage(THREE.DynamicDrawUsage);
    this.aCol = new THREE.BufferAttribute(new Float32Array(nV * 4), 4).setUsage(THREE.DynamicDrawUsage);
    this.aStyle = new THREE.BufferAttribute(new Float32Array(nV), 1).setUsage(THREE.DynamicDrawUsage);
    this.geo.setAttribute('position', this.aPos);
    this.geo.setAttribute('aUv', this.aUv);
    this.geo.setAttribute('aColor', this.aCol);
    this.geo.setAttribute('aStyle', this.aStyle);
    for (const a of [this.aPos, this.aUv, this.aCol, this.aStyle]) persistentRange(a);
    const idx = new (nV > 65535 ? Uint32Array : Uint16Array)(maxTrails * (this.MAXP - 1) * 6);
    let k = 0;
    for (let s = 0; s < maxTrails; s++) {
      for (let j = 0; j < this.MAXP - 1; j++) {
        const a = (s * this.MAXP + j) * 2;
        idx[k++] = a;
        idx[k++] = a + 1;
        idx[k++] = a + 2;
        idx[k++] = a + 1;
        idx[k++] = a + 3;
        idx[k++] = a + 2;
      }
    }
    this.geo.setIndex(new THREE.BufferAttribute(idx, 1));
    this.geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this.mat = new THREE.ShaderMaterial({
      uniforms: { uFx: uniforms.uFx, uTime: uniforms.uTime },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    });
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 9;
    this.mesh.visible = false;
  }

  alloc(head: RGB, tail: RGB, styleId: number, fade: number, alpha: number): number {
    if (this.freeTop === 0) return -1;
    const id = this.freeList[--this.freeTop]!;
    this.used[id] = 1;
    this.sampling[id] = 1;
    this.count[id] = 0;
    const p = id * 9;
    const P = this.par;
    P[p] = head[0];
    P[p + 1] = head[1];
    P[p + 2] = head[2];
    P[p + 3] = tail[0];
    P[p + 4] = tail[1];
    P[p + 5] = tail[2];
    P[p + 6] = styleId;
    P[p + 7] = Math.max(0.02, fade);
    P[p + 8] = alpha;
    this.live++;
    return id;
  }

  /** Stop sampling; the trail fades out and frees itself. */
  release(id: number): void {
    if (id >= 0 && id < this.maxTrails) this.sampling[id] = 0;
  }

  push(id: number, tx: number, ty: number, tz: number, ix: number, iy: number, iz: number): void {
    if (id < 0 || !this.used[id] || !this.sampling[id]) return;
    const S = this.maxSamples;
    const base = id * S;
    let n = this.count[id]!;
    if (n === 0 || this.sAge[base]! >= MIN_SAMPLE_DT) {
      // Shift older samples back by one (newest first).
      const m = Math.min(n, S - 1);
      for (let j = m; j > 0; j--) {
        const d = base + j;
        const s = d - 1;
        this.sTip[d * 3] = this.sTip[s * 3]!;
        this.sTip[d * 3 + 1] = this.sTip[s * 3 + 1]!;
        this.sTip[d * 3 + 2] = this.sTip[s * 3 + 2]!;
        this.sIn[d * 3] = this.sIn[s * 3]!;
        this.sIn[d * 3 + 1] = this.sIn[s * 3 + 1]!;
        this.sIn[d * 3 + 2] = this.sIn[s * 3 + 2]!;
        this.sAge[d] = this.sAge[s]!;
      }
      n = m + 1;
      this.count[id] = n;
      this.sAge[base] = 0;
    }
    this.sTip[base * 3] = tx;
    this.sTip[base * 3 + 1] = ty;
    this.sTip[base * 3 + 2] = tz;
    this.sIn[base * 3] = ix;
    this.sIn[base * 3 + 1] = iy;
    this.sIn[base * 3 + 2] = iz;
  }

  /** Build geometry from the current samples, then age them by `dt`. */
  update(dt: number): void {
    const P = this.aPos.array as Float32Array;
    const UV = this.aUv.array as Float32Array;
    const CO = this.aCol.array as Float32Array;
    const ST = this.aStyle.array as Float32Array;
    const S = this.maxSamples;
    const SUB = this.sub;
    const MAXP = this.MAXP;
    let maxTrail = -1;
    let dirty = false;
    for (let s = 0; s < this.maxTrails; s++) {
      if (!this.used[s]) continue;
      const n = this.count[s]!;
      const base = s * S;
      const pp = s * 9;
      const fade = this.par[pp + 7]!;
      const alpha = this.par[pp + 8]!;
      const style = this.par[pp + 6]!;
      let m = 0;
      if (n >= 2) {
        for (let i = 0; i < n - 1; i++) {
          const i0 = base + Math.max(0, i - 1);
          const i1 = base + i;
          const i2 = base + i + 1;
          const i3 = base + Math.min(n - 1, i + 2);
          const a1 = this.sAge[i1]!;
          const a2 = this.sAge[i2]!;
          for (let k = 0; k < SUB; k++) {
            const f = k / SUB;
            this.writePair(s, m++, i0, i1, i2, i3, f, a1 + (a2 - a1) * f, fade, alpha, style, P, UV, CO, ST);
          }
        }
        const last = base + n - 1;
        this.writePair(s, m++, last, last, last, last, 0, this.sAge[last]!, fade, alpha, style, P, UV, CO, ST);
      }
      // Collapse pairs left over from a longer trail last frame (zero-area, zero alpha).
      const prev = this.lastPairs[s]!;
      if (prev > m) {
        const v0 = (s * MAXP + Math.max(0, m - 1)) * 2;
        const x = P[v0 * 3] ?? 0;
        const y = P[v0 * 3 + 1] ?? 0;
        const z = P[v0 * 3 + 2] ?? 0;
        for (let j = m; j < prev; j++) {
          const v = (s * MAXP + j) * 2;
          for (let q = 0; q < 2; q++) {
            P[(v + q) * 3] = x;
            P[(v + q) * 3 + 1] = y;
            P[(v + q) * 3 + 2] = z;
            CO[(v + q) * 4 + 3] = 0;
          }
        }
        dirty = true;
      }
      if (n === 0 && m === 0 && prev === 0 && !this.sampling[s]) {
        // Faded out: free the slot.
        this.used[s] = 0;
        this.freeList[this.freeTop++] = s;
        this.live--;
        continue;
      }
      this.lastPairs[s] = m;
      if (m > 0) dirty = true;
      maxTrail = s;
      // Age and drop expired samples (oldest last).
      for (let j = 0; j < n; j++) this.sAge[base + j] = this.sAge[base + j]! + dt;
      // Keep one sample past the fade edge (alpha 0 there) so the tail never pops.
      let keep = n;
      while (keep > 0 && this.sAge[base + keep - 1]! > fade && (keep < 2 || this.sAge[base + keep - 2]! > fade)) keep--;
      this.count[s] = keep;
    }
    const verts = (maxTrail + 1) * MAXP * 2;
    this.mesh.visible = maxTrail >= 0;
    this.geo.setDrawRange(0, (maxTrail + 1) * (MAXP - 1) * 6);
    if (dirty && verts > 0) {
      upload(this.aPos, verts * 3);
      upload(this.aUv, verts * 2);
      upload(this.aCol, verts * 4);
      upload(this.aStyle, verts);
    }
  }

  private writePair(s: number, j: number, i0: number, i1: number, i2: number, i3: number, f: number, age: number, fade: number, alpha: number, style: number, P: Float32Array, UV: Float32Array, CO: Float32Array, ST: Float32Array): void {
    const u = Math.min(1, Math.max(0, age / fade));
    const v = (s * this.MAXP + j) * 2;
    const T = this.sTip;
    const I = this.sIn;
    // Catmull-Rom (uniform) between samples i1 → i2.
    const f2 = f * f;
    const f3 = f2 * f;
    const c0 = -0.5 * f3 + f2 - 0.5 * f;
    const c1 = 1.5 * f3 - 2.5 * f2 + 1;
    const c2 = -1.5 * f3 + 2 * f2 + 0.5 * f;
    const c3 = 0.5 * f3 - 0.5 * f2;
    const narrow = 1 - 0.45 * u;
    for (let a = 0; a < 3; a++) {
      const t = T[i0 * 3 + a]! * c0 + T[i1 * 3 + a]! * c1 + T[i2 * 3 + a]! * c2 + T[i3 * 3 + a]! * c3;
      const i = I[i0 * 3 + a]! * c0 + I[i1 * 3 + a]! * c1 + I[i2 * 3 + a]! * c2 + I[i3 * 3 + a]! * c3;
      P[v * 3 + a] = t;
      P[(v + 1) * 3 + a] = t + (i - t) * narrow;
    }
    UV[v * 2] = u;
    UV[v * 2 + 1] = 0;
    UV[(v + 1) * 2] = u;
    UV[(v + 1) * 2 + 1] = 1;
    const k = 1 - u;
    const al = alpha * k * Math.sqrt(k);
    const pp = s * 9;
    const r = this.par[pp]! + (this.par[pp + 3]! - this.par[pp]!) * u;
    const g = this.par[pp + 1]! + (this.par[pp + 4]! - this.par[pp + 1]!) * u;
    const b = this.par[pp + 2]! + (this.par[pp + 5]! - this.par[pp + 2]!) * u;
    for (let q = 0; q < 2; q++) {
      const c = (v + q) * 4;
      CO[c] = r;
      CO[c + 1] = g;
      CO[c + 2] = b;
      CO[c + 3] = al;
      ST[v + q] = style;
    }
  }

  clear(): void {
    for (let s = 0; s < this.maxTrails; s++) {
      this.count[s] = 0;
      this.sampling[s] = 0;
    }
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geo.dispose();
    this.mat.dispose();
  }
}

/** Opaque trail handle: < ADD_SLOTS additive, else alpha-blended. -1 = none. */
const ADD_SLOTS = 1024;

export interface TrailParams {
  color: RGB;
  tail: RGB;
  styleId: number;
  fade: number;
  alpha: number;
  additive: boolean;
}

export class BladeTrails {
  readonly group = new THREE.Group();
  private readonly uFx = { value: 1 };
  private readonly uTime = { value: 0 };
  private readonly add: TrailBatch;
  private readonly blend: TrailBatch;

  /** `maxSamples` points per trail (×`sub` after smoothing): the segment cap. */
  constructor(maxTrails = 16, maxSamples = 24, sub = 4) {
    const u = { uFx: this.uFx, uTime: this.uTime };
    this.add = new TrailBatch(maxTrails, maxSamples, sub, u, true);
    this.blend = new TrailBatch(Math.max(4, maxTrails >> 1), maxSamples, sub, u, false);
    this.group.add(this.blend.mesh, this.add.mesh);
    this.group.name = 'blade-trails';
  }

  /** Start a ribbon; returns a handle (-1 when the pool is full: the ribbon is simply skipped). */
  start(p: TrailParams): number {
    if (p.additive) return this.add.alloc(p.color, p.tail, p.styleId, p.fade, p.alpha);
    const id = this.blend.alloc(p.color, p.tail, p.styleId, p.fade, p.alpha);
    return id < 0 ? -1 : ADD_SLOTS + id;
  }

  /** Add the current tip / inner-edge points (world space). */
  push(h: number, tip: THREE.Vector3, inner: THREE.Vector3): void {
    if (h < 0) return;
    const b = h >= ADD_SLOTS ? this.blend : this.add;
    b.push(h >= ADD_SLOTS ? h - ADD_SLOTS : h, tip.x, tip.y, tip.z, inner.x, inner.y, inner.z);
  }

  /** Stop sampling (the ribbon fades out over its fade time). */
  release(h: number): void {
    if (h < 0) return;
    if (h >= ADD_SLOTS) this.blend.release(h - ADD_SLOTS);
    else this.add.release(h);
  }

  /** After all heroes pushed this frame: rebuild, then age by `dt` (animation time: hit-stop slows it). */
  update(dt: number, intensity: number): void {
    this.uTime.value += dt;
    this.uFx.value = Math.min(1.15, Math.max(0, intensity));
    this.add.update(dt);
    this.blend.update(dt);
    this.group.visible = intensity > 0.01 && (this.add.live > 0 || this.blend.live > 0);
  }

  /** Make the (empty) meshes visible for a shader prewarm compile, then restore. */
  warm(on: boolean): void {
    this.group.visible = on || this.live > 0;
    this.add.mesh.visible = on || this.add.live > 0;
    this.blend.mesh.visible = on || this.blend.live > 0;
  }

  get live(): number {
    return this.add.live + this.blend.live;
  }

  clear(): void {
    this.add.clear();
    this.blend.clear();
  }

  dispose(): void {
    this.add.dispose();
    this.blend.dispose();
    this.group.removeFromParent();
  }
}
